import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Image,
  SimpleGrid,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  ViewEInvoiceData,
  DownloadEInvoicePDF,
} from 'reactRedux/thunks/eInvoiceThunk/eInvoiceThunk';
import PageLoader from 'components/wholePageLoader/PageLoader';
import moment from 'moment';
import { FaEdit, FaFilePdf } from 'react-icons/fa';
import { useForm, Controller } from 'react-hook-form';
import CommonInput from 'components/commonInput/CommonInput';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import {
  countries,
  validatePincode,
  getPincodeFormat,
} from 'utils/CountryStateData';
import { getProvinces } from 'localized-regions';
import PropTypes from 'prop-types';
import BackButton from 'components/backButton/BackButton';

// Province options in the { id, label } shape the dropdown expects, sourced from
// localized-regions (single source of truth). Spain returns the 50 provinces.
const provinceOptionsFor = (countryCode) =>
  countryCode
    ? getProvinces(countryCode).map((p) => ({ id: p.code, label: p.name }))
    : [];

const CustomerInvoiceDetail = () => {
  const { invoiceDetailId } = useParams();
  const invoiceType = 'B2C'; // Customer invoices
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { textColor, brandStars } = useColorModeVariables();
  const { viewEInvoiceDetails, isViewLoading, isDownloadLoading } = useSelector(
    (state) => state.EInvoiceSlice,
  );

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [, setSelectedState] = useState(null);
  const [availableStates, setAvailableStates] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      legalName: '',
      taxNumber: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      country: null,
      state: null,
      pincode: '',
    },
  });

  const watchCountry = watch('country');

  useEffect(() => {
    if (invoiceDetailId) {
      dispatch(ViewEInvoiceData({ id: invoiceDetailId, invoiceType }));
    }
  }, [invoiceDetailId, invoiceType, dispatch]);

  const handleDownloadPDF = () => {
    dispatch(
      DownloadEInvoicePDF({
        id: invoiceDetailId,
        invoiceType: invoiceType,
        invoiceId: viewEInvoiceDetails?.invoiceId,
      }),
    );
  };

  // Update available states when country changes
  useEffect(() => {
    if (watchCountry) {
      const states = provinceOptionsFor(watchCountry.code);
      setAvailableStates(states);
      setSelectedCountry(watchCountry);
      // Reset state when country changes
      setValue('state', null);
      setSelectedState(null);
    } else {
      setAvailableStates([]);
      setSelectedCountry(null);
      setSelectedState(null);
    }
  }, [watchCountry, setValue]);

  // Populate form when modal opens
  useEffect(() => {
    if (isOpen && viewEInvoiceDetails) {
      const countryCode = viewEInvoiceDetails?.jsonData?.country || '';
      const stateCode = viewEInvoiceDetails?.jsonData?.state || '';

      const country = countries.find((c) => c.code === countryCode);
      const states = provinceOptionsFor(countryCode);
      // Fallback so a legacy stored value (old autonomous-community code) still
      // displays instead of showing a blank dropdown.
      const state =
        states.find((s) => s.id === stateCode) ||
        (stateCode ? { id: stateCode, label: stateCode } : null);

      const formData = {
        legalName:
          viewEInvoiceDetails?.jsonData?.fiskalyResponse?.metadata
            ?.legal_name ||
          viewEInvoiceDetails?.jsonData?.name ||
          '',
        taxNumber:
          viewEInvoiceDetails?.jsonData?.fiskalyResponse?.metadata
            ?.tax_number ||
          viewEInvoiceDetails?.jsonData?.tax_number ||
          '',
        addressLine1:
          viewEInvoiceDetails?.jsonData?.fiskalyResponse?.metadata
            ?.legal_address ||
          viewEInvoiceDetails?.jsonData?.address_line ||
          '',
        addressLine2: '',
        city: viewEInvoiceDetails?.jsonData?.city || '',
        country: country || null,
        state: state || null,
        pincode:
          viewEInvoiceDetails?.jsonData?.fiskalyResponse?.metadata
            ?.postal_code ||
          viewEInvoiceDetails?.jsonData?.postal_code ||
          '',
      };

      if (country) {
        setSelectedCountry(country);
        setAvailableStates(states);
      }

      if (state) {
        setSelectedState(state);
      }

      reset(formData);
    }
  }, [isOpen, viewEInvoiceDetails, reset]);

  const onSubmit = () => {
    // TODO: Implement update API call
    onClose();
  };

  const handleEditClick = () => {
    onOpen();
  };

  const DetailItem = ({ label, value }) => (
    <Flex
      alignItems={{ sm: 'start', md: 'center' }}
      direction={{ sm: 'column', md: 'row' }}
      gap="10px"
      padding="4px 8px"
      borderLeft={'1px solid #ccc'}
    >
      <Text fontWeight="normal">{label} :</Text>
      <Text fontWeight="semibold" wordBreak="break-word">
        {value || 'N/A'}
      </Text>
    </Flex>
  );

  DetailItem.propTypes = {
    label: PropTypes.string,
    value: PropTypes.node,
  };

  const qrData =
    viewEInvoiceDetails?.jsonData?.fiskalyResponse?.content?.compliance?.code
      ?.image?.data;

  return (
    <>
      {isViewLoading ? (
        <PageLoader />
      ) : viewEInvoiceDetails ? (
        <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
          {/* Action Buttons */}
          <Flex justifyContent="space-between" mb={4} gap="3">
            <BackButton
              fallbackUrl="/admin/e-invoices"
              label="Back to E-Invoices"
            />
            <Flex gap="3">
              <Button
                py="10px"
                px="15px"
                fontWeight="semibold"
                borderRadius="8px"
                bg="primary"
                color="white"
                _hover={{ bg: 'primary', opacity: 0.8 }}
                type="button"
                leftIcon={<FaEdit size={22} />}
                onClick={handleEditClick}
              >
                Edit Detail
              </Button>
              <Button
                py="10px"
                px="15px"
                fontWeight="semibold"
                borderRadius="8px"
                bg="primary"
                color="white"
                _hover={{ bg: 'primary', opacity: 0.8 }}
                type="button"
                leftIcon={<FaFilePdf size={25} />}
                onClick={handleDownloadPDF}
                disabled={isDownloadLoading}
                isLoading={isDownloadLoading}
                loadingText="Downloading..."
              >
                Download PDF
              </Button>
              {qrData && (
                <Image
                  src={`data:image/png;base64,${qrData}`}
                  boxSize="100px"
                  alt="QR Code"
                />
              )}
            </Flex>
          </Flex>

          {/* Edit Customer Modal */}
          <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Request For Enrichment Invoice</ModalHeader>
              <ModalCloseButton />
              <form onSubmit={handleSubmit(onSubmit)}>
                <ModalBody>
                  <Text fontSize="sm" color="gray.600" mb={4}>
                    Please provide your details for enrichment invoice.
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <CommonInput
                      type="text"
                      name="legalName"
                      labelText="Legal Name"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Enter legal name"
                      register={register('legalName', {
                        required: 'Legal name is required',
                      })}
                      errors={errors?.legalName}
                      isValidate={true}
                    />

                    <CommonInput
                      type="text"
                      name="taxNumber"
                      labelText="Tax Number"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Enter tax number"
                      register={register('taxNumber', {
                        required: 'Tax number is required',
                      })}
                      errors={errors?.taxNumber}
                      isValidate={true}
                    />

                    <CommonInput
                      type="text"
                      name="addressLine1"
                      labelText="Address Line 1"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Enter address line 1"
                      register={register('addressLine1', {
                        required: 'Address line 1 is required',
                      })}
                      errors={errors?.addressLine1}
                      isValidate={true}
                    />

                    <CommonInput
                      type="text"
                      name="addressLine2"
                      labelText="Address Line 2"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Enter address line 2"
                      register={register('addressLine2')}
                      errors={errors?.addressLine2}
                      isValidate={false}
                    />

                    <CommonInput
                      type="text"
                      name="city"
                      labelText="City"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Enter city"
                      register={register('city', {
                        required: 'City is required',
                      })}
                      errors={errors?.city}
                      isValidate={true}
                    />

                    <Controller
                      name="country"
                      control={control}
                      rules={{ required: 'Country is required' }}
                      render={({ field }) => (
                        <CommonReactSelect
                          label="Country"
                          isRequired={true}
                          type="select"
                          options={countries}
                          value={field.value}
                          onChange={(selected) => {
                            field.onChange(selected);
                          }}
                          error={errors?.country}
                          register={register}
                          name="country"
                          getOptionLabel={(option) => option.label}
                          getOptionValue={(option) => option.code}
                          placeholderValue="country"
                        />
                      )}
                    />

                    <Controller
                      name="state"
                      control={control}
                      rules={{ required: 'State is required' }}
                      render={({ field }) => (
                        <CommonReactSelect
                          label="State"
                          isRequired={true}
                          type="select"
                          options={availableStates}
                          value={field.value}
                          onChange={(selected) => {
                            field.onChange(selected);
                            setSelectedState(selected);
                          }}
                          error={errors?.state}
                          register={register}
                          name="state"
                          getOptionLabel={(option) => option.label}
                          getOptionValue={(option) => option.id}
                          isDisabled={!selectedCountry}
                          placeholderValue={
                            selectedCountry ? 'state' : 'Select country first'
                          }
                        />
                      )}
                    />

                    <CommonInput
                      type="text"
                      name="pincode"
                      labelText="Pincode"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder={
                        selectedCountry
                          ? `Enter pincode (e.g., ${getPincodeFormat(selectedCountry.code)})`
                          : 'Enter pincode'
                      }
                      register={register('pincode', {
                        required: 'Pincode is required',
                        validate: (value) => {
                          if (!selectedCountry) return true;
                          const isValid = validatePincode(
                            value,
                            selectedCountry.code,
                          );
                          return (
                            isValid ||
                            `Invalid pincode format. Expected format: ${getPincodeFormat(selectedCountry.code)}`
                          );
                        },
                      })}
                      errors={errors?.pincode}
                      isValidate={true}
                    />
                  </SimpleGrid>
                </ModalBody>

                <ModalFooter>
                  <Button variant="outline" mr={3} onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    bg="primary"
                    color="white"
                    _hover={{ bg: 'primary', opacity: 0.8 }}
                  >
                    Submit Request
                  </Button>
                </ModalFooter>
              </form>
            </ModalContent>
          </Modal>

          {/* E-Invoice Details Card */}
          <Flex direction="column">
            <Card
              w="100%"
              boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
              borderRadius="20px"
              paddingInline="40px"
              paddingBlock="40px"
              overflowX={{ sm: 'scroll', lg: 'hidden' }}
            >
              {/* Invoice subtype badge */}
              <Flex mb={4} gap={2}>
                {viewEInvoiceDetails?.jsonData?.isRefundInvoice && (
                  <Badge
                    colorScheme="red"
                    fontSize="sm"
                    px={3}
                    py={1}
                    borderRadius="md"
                  >
                    Refund / Correcting Invoice
                  </Badge>
                )}
                {viewEInvoiceDetails?.jsonData?.isEnrichmentInvoice && (
                  <Badge
                    colorScheme="blue"
                    fontSize="sm"
                    px={3}
                    py={1}
                    borderRadius="md"
                  >
                    Enrichment Invoice
                  </Badge>
                )}
                {!viewEInvoiceDetails?.jsonData?.isRefundInvoice &&
                  !viewEInvoiceDetails?.jsonData?.isEnrichmentInvoice && (
                    <Badge
                      colorScheme="green"
                      fontSize="sm"
                      px={3}
                      py={1}
                      borderRadius="md"
                    >
                      Simple Invoice
                    </Badge>
                  )}
              </Flex>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                <DetailItem
                  label="E-Invoice ID"
                  value={viewEInvoiceDetails?.invoiceId}
                />
                {viewEInvoiceDetails?.fiskalyInvoiceId && (
                  <DetailItem
                    label="Fiskaly Invoice ID"
                    value={viewEInvoiceDetails?.fiskalyInvoiceId}
                  />
                )}
                <DetailItem
                  label="Previous Invoice ID"
                  value={
                    viewEInvoiceDetails?.jsonData?.previousInvoiceId || 'N/A'
                  }
                />
                <DetailItem
                  label="Next Invoice ID"
                  value={viewEInvoiceDetails?.jsonData?.nextInvoiceId || 'N/A'}
                />
                <DetailItem
                  label="Transaction Id"
                  value={viewEInvoiceDetails?.transactionId}
                />
                <DetailItem
                  label="Amount"
                  value={
                    viewEInvoiceDetails?.jsonData?.total !== undefined
                      ? `${viewEInvoiceDetails.region?.currencySymbol ?? viewEInvoiceDetails.jsonData.currencySymbol ?? '€'}${Number(viewEInvoiceDetails.jsonData.total).toFixed(2)}`
                      : 'N/A'
                  }
                />
                <DetailItem
                  label="Tax Amount"
                  value={
                    viewEInvoiceDetails?.jsonData?.taxAmount !== undefined
                      ? `${viewEInvoiceDetails.region?.currencySymbol ?? viewEInvoiceDetails.jsonData.currencySymbol ?? '€'}${Number(viewEInvoiceDetails.jsonData.taxAmount).toFixed(2)}`
                      : 'N/A'
                  }
                />
                <DetailItem
                  label="Tax Rate"
                  value={
                    viewEInvoiceDetails?.jsonData?.tax
                      ? `${viewEInvoiceDetails.jsonData.tax}%`
                      : 'N/A'
                  }
                />
                <DetailItem
                  label="Invoice Type"
                  value={viewEInvoiceDetails?.invoiceType === 1 ? 'B2B' : 'B2C'}
                />
                <DetailItem
                  label="Created At"
                  value={
                    viewEInvoiceDetails?.createdAt
                      ? moment(viewEInvoiceDetails.createdAt).format(
                          'DD/MM/YYYY',
                        )
                      : 'N/A'
                  }
                />
              </SimpleGrid>
            </Card>

            {/* Receiver (Merchant) Information */}
            <Card
              w="100%"
              boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
              borderRadius="20px"
              paddingInline="40px"
              paddingBlock="40px"
              overflowX={{ sm: 'scroll', lg: 'hidden' }}
              mt={4}
            >
              <Text fontSize="lg" fontWeight="600" mb={4}>
                Receiver (Customer) Information
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                <DetailItem
                  label="Customer Name"
                  value={viewEInvoiceDetails?.jsonData?.name}
                />
                <DetailItem
                  label="Address"
                  value={viewEInvoiceDetails?.jsonData?.address_line}
                />
                <DetailItem
                  label="Postal code"
                  value={viewEInvoiceDetails?.jsonData?.postal_code || 'N/A'}
                />{' '}
                <DetailItem
                  label="Tax number"
                  value={viewEInvoiceDetails?.jsonData?.tax_number || 'N/A'}
                />
                <DetailItem
                  label="contact"
                  value={viewEInvoiceDetails?.jsonData?.contact || 'N/A'}
                />
              </SimpleGrid>
            </Card>

            {/* Sender (Merchant) Information */}
            <Card
              w="100%"
              boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
              borderRadius="20px"
              paddingInline="40px"
              paddingBlock="40px"
              overflowX={{ sm: 'scroll', lg: 'hidden' }}
              mt={4}
            >
              <Text fontSize="lg" fontWeight="600" mb={4}>
                Sender (Merchant) Information
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={4}>
                <DetailItem
                  label="Merchant ID"
                  value={viewEInvoiceDetails?.merchant?.merchantId}
                />
                <DetailItem
                  label="Merchant Name"
                  value={viewEInvoiceDetails?.merchant?.name}
                />
                <DetailItem
                  label="Address"
                  value={viewEInvoiceDetails?.merchant?.address}
                />
                <DetailItem
                  label="Currency"
                  value={viewEInvoiceDetails?.region?.currency}
                />
                <DetailItem
                  label="Region"
                  value={viewEInvoiceDetails?.region?.regionName}
                />
              </SimpleGrid>
            </Card>
          </Flex>
        </Box>
      ) : (
        <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
          <Card p={6} width="100%">
            <Text textAlign="center" color="gray.500">
              No invoice details found
            </Text>
          </Card>
        </Box>
      )}
    </>
  );
};

export default CustomerInvoiceDetail;
